<template>
    <div class="leave-wallchart-grid">
        <!-- Chart -->
        <div class="wc-scroll">
            <div class="wc-grid" :style="{ minWidth: gridMinWidth + 'px' }">
                <!-- Weekday header -->
                <div class="wc-row wc-header">
                    <div class="wc-left wc-left-header"></div>
                    <div class="wc-track">
                        <div
                            v-for="d in days"
                            :key="'h' + d.key"
                            class="wc-hcell"
                            :class="{ weekend: d.isWeekend, highlight: isHighlightedDay(d), 'wc-note-click': notesEnabled }"
                            :title="notesEnabled ? 'Company note for this day' : null"
                            @click="notesEnabled && $emit('day-note-click', d)"
                        >
                            <span :class="{ 'wc-today': d.isToday }">{{ d.letter }}</span>
                            <i v-if="notesEnabled && noteCount(0, d.key)" class="wc-note-marker" aria-hidden="true"></i>
                        </div>
                    </div>
                </div>

                <!-- Empty state -->
                <div v-if="rows.length === 0" class="wc-empty">
                    <v-icon size="48" color="grey lighten-1">mdi-account-off</v-icon>
                    <p class="grey--text mt-2 mb-0">{{ emptyText }}</p>
                </div>

                <!-- Rows -->
                <div
                    v-for="row in rows"
                    :key="row.user.id"
                    class="wc-row wc-body-row"
                    :class="{ 'highlight-row': row.user.id === highlightUserId }"
                >
                    <div class="wc-left wc-left-click" @click="$emit('row-click', row.user)" title="Open calendar">
                        <div class="wc-avatar">
                            <v-avatar size="40" color="#e0e0e0">
                                <v-img v-if="row.pic" :src="row.pic" />
                                <span v-else class="wc-initials">{{ initials(row.name) }}</span>
                            </v-avatar>
                            <span v-if="row.remaining !== null" class="wc-badge">{{ row.remaining }}</span>
                        </div>
                        <div class="wc-left-text">
                            <div class="wc-name">{{ row.name }}</div>
                            <div class="wc-position">{{ row.position }}</div>
                        </div>
                    </div>
                    <div class="wc-track">
                        <div
                            v-for="(c, i) in row.cells"
                            :key="'c' + row.user.id + i"
                            class="wc-day"
                            :class="{
                                weekend: c.weekend,
                                leave: !!c.leave && !c.isHalf,
                                half: c.isHalf,
                                wfh: !!c.leave && c.isWfh,
                                pending: !!c.leave && c.isPending,
                                'wc-note-click': notesEnabled,
                            }"
                            :style="cellStyle(c)"
                            @mouseenter="onCellEnter($event, c, row.user.id, days[i].key)"
                            @mouseleave="hoverTip = null"
                            @click="onCellClick(c, row.user, days[i])"
                        >
                            <span class="wc-daynum">{{ c.dayNum }}</span>
                            <button
                                v-if="canAddFor(row.user.id) && !c.leave"
                                type="button"
                                class="wc-add"
                                title="Add leave"
                                @click.stop="$emit('cell-add-leave', { user: row.user, day: days[i] })"
                            >+</button>
                            <span v-if="c.leave && c.isWfh" class="wc-wfh-glyph" aria-hidden="true">⌂</span>
                            <i v-if="notesEnabled && noteCount(row.user.id, days[i].key)" class="wc-note-marker" aria-hidden="true"></i>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Hover tooltip -->
        <div v-if="hoverTip" class="wc-tip" :style="{ left: hoverTip.x + 'px', top: hoverTip.y + 'px', whiteSpace: 'pre-line' }">
            {{ hoverTip.text }}
        </div>

        <!-- Legend -->
        <div class="wc-legend mt-3">
            <span v-if="legendTypes.length === 0" class="grey--text text-caption">
                {{ legendEmptyText }}
            </span>
            <span v-for="lt in legendTypes" :key="'l' + lt.id" class="wc-legend-item">
                <i
                    v-if="isWfhLegend(lt)"
                    class="wc-legend-wfh"
                    :style="{ backgroundColor: colorForType(lt.id) }"
                >⌂</i>
                <i v-else :style="{ backgroundColor: colorForType(lt.id) }"></i>{{ lt.name }}
            </span>
        </div>
        <div v-if="statusLegend" class="wc-legend wc-legend-status mt-2">
            <span class="wc-legend-item"><i class="wc-sw-pending"></i>Pending (awaiting approval)</span>
            <span class="wc-legend-item"><i class="wc-sw-half"></i>Half day</span>
            <span class="wc-legend-item"><i class="wc-sw-weekend"></i>Weekend</span>
            <span v-if="notesEnabled" class="wc-legend-item"><i class="wc-sw-note"></i>Has a note</span>
        </div>
    </div>
</template>

<script>
import { isWfhType, initials as buildInitials, buildNoteIndex } from './leaveUtils';

const MIN_CELL_W = 30;
const LEFT_W = 230;

export default {
    name: 'LeaveWallchartGrid',
    props: {
        days: { type: Array, required: true },
        rows: { type: Array, default: () => [] },
        legendTypes: { type: Array, default: () => [] },
        colorForType: { type: Function, required: true },
        highlightUserId: { default: null },
        highlightFrom: { type: String, default: null }, // 'YYYY-MM-DD'
        highlightTo: { type: String, default: null },
        emptyText: { type: String, default: 'No employees match your filters' },
        legendEmptyText: { type: String, default: 'No approved leave this month' },
        notes: { type: Array, default: () => [] },
        notesEnabled: { type: Boolean, default: false },
        // Extra legend row: pending, half day, weekend, note marker.
        statusLegend: { type: Boolean, default: false },
        // hover "+" on free cells to log leave; addableUserIds limits which rows (null = all)
        canAddLeave: { type: Boolean, default: false },
        addableUserIds: { type: Array, default: null },
    },
    data() {
        return { leftW: LEFT_W, hoverTip: null };
    },
    computed: {
        gridMinWidth() {
            return this.leftW + this.days.length * MIN_CELL_W;
        },
        noteIndex() {
            return this.notesEnabled ? buildNoteIndex(this.notes) : {};
        },
    },
    methods: {
        canAddFor(userId) {
            if (!this.canAddLeave) return false;
            return !this.addableUserIds || this.addableUserIds.includes(Number(userId));
        },
        isHighlightedDay(d) {
            return !!(this.highlightFrom && this.highlightTo && d.key >= this.highlightFrom && d.key <= this.highlightTo);
        },
        isWfhLegend(lt) {
            return isWfhType(lt);
        },
        initials(name) {
            return buildInitials(name);
        },
        cellStyle(c) {
            if (!c.leave) return {};
            // Work From Home is a flat block in its own colour, like any leave
            // type — the ⌂ glyph is what marks it as "in, working remotely".
            if (c.isHalf) {
                const stops = c.side === 'pm'
                    ? `transparent 0 50%, ${c.color} 50% 100%`
                    : `${c.color} 0 50%, transparent 50% 100%`;
                return { backgroundImage: `linear-gradient(to right, ${stops})` };
            }
            return { backgroundColor: c.color };
        },
        formatDate(s) {
            return s ? this.$date(s.substring(0, 10)).format('MMM D, YYYY') : '';
        },
        formatRange(r) {
            const f = this.formatDate(r.from);
            const t = this.formatDate(r.to);
            if (r.is_half_day) return `${f} (${r.half_day_period === 'PM' ? 'Afternoon' : 'Morning'})`;
            return f === t ? f : `${f} – ${t}`;
        },
        formatDuration(r) {
            if (r.is_half_day) return '0.5 day';
            const n = r.count || 0;
            return `${n} day${n === 1 ? '' : 's'}`;
        },
        cellTitle(c) {
            const r = c.leave;
            const type = (r.leave_type && r.leave_type.name) || 'Leave';
            const pending = c.isPending ? ' [pending]' : '';
            return `${type}${pending} · ${this.formatRange(r)} (${this.formatDuration(r)})`;
        },
        notesFor(userId, dayKey) {
            return this.noteIndex[`${Number(userId) || 0}|${dayKey}`] || [];
        },
        noteCount(userId, dayKey) {
            return this.notesFor(userId, dayKey).length;
        },
        noteLines(userId, dayKey) {
            return this.notesFor(userId, dayKey).map((n) => `${n.is_private ? '🔒 Private' : '📝'} ${(n.author && n.author.name) || 'Manager'}: ${n.body}`);
        },
        onCellEnter(e, c, userId, dayKey) {
            const lines = [];
            if (c.leave) lines.push(this.cellTitle(c));
            if (this.notesEnabled) lines.push(...this.noteLines(userId, dayKey));
            if (!lines.length) return;
            const pad = 14;
            let x = e.clientX + pad;
            const y = e.clientY + pad;
            if (x > window.innerWidth - 260) x = e.clientX - 260;
            this.hoverTip = { text: lines.join('\n'), x, y };
        },
        onCellClick(c, user, day) {
            if (this.notesEnabled) {
                this.$emit('cell-note-click', { user, day, leave: c.leave || null });
            } else if (c.leave) {
                this.$emit('cell-click', c.leave);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.wc-scroll {
    overflow: auto;
    max-height: 70vh;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #fff;
}

.wc-grid {
    position: relative;
}

.wc-row {
    display: flex;
    width: 100%;
    border-bottom: 1px solid #f2f2f2;
}

.wc-header {
    position: sticky;
    top: 0;
    z-index: 4;
    background: #fff;
    border-bottom: 1px solid #e0e0e0;
}

.wc-left {
    position: sticky;
    left: 0;
    z-index: 3;
    flex: none;
    width: 230px;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px;
    background: #fff;
    border-right: 1px solid #eee;
}

.wc-left-header {
    z-index: 5;
    padding: 0;
    height: 38px;
}

.wc-avatar {
    position: relative;
    flex: none;
}

.wc-badge {
    position: absolute;
    top: -5px;
    left: -5px;
    background: #3f3f56;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    min-width: 18px;
    height: 18px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
    line-height: 1;
}

.wc-initials {
    color: #616161;
    font-size: 12px;
    font-weight: 700;
}

.wc-left-text {
    overflow: hidden;
}

.wc-name {
    font-size: 13px;
    font-weight: 600;
    color: #1976d2;
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.wc-position {
    font-size: 11px;
    color: #9e9e9e;
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.wc-track {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
}

.wc-hcell {
    flex: 1 1 0;
    min-width: 30px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px 0;
    font-size: 11px;
    color: #9e9e9e;
    position: relative;

    &.weekend {
        color: #bdbdbd;
    }
}

.wc-today {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 1.5px solid #1976d2;
    border-radius: 50%;
    color: #1976d2;
    font-weight: 700;
}

.wc-body-row:hover .wc-left {
    background: #fafafa;
}

.wc-left-click {
    cursor: pointer;
}

.wc-left-click:hover .wc-name {
    text-decoration: underline;
}

.wc-day {
    flex: 1 1 0;
    min-width: 30px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: #9e9e9e;
    position: relative;
    user-select: none;
}

.wc-add {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 16px;
    height: 16px;
    line-height: 14px;
    border-radius: 50%;
    border: 1px solid #1976d2;
    background: #fff;
    color: #1976d2;
    font-size: 12px;
    font-weight: 700;
    padding: 0;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.12s;
    z-index: 2;
}

.wc-day:hover .wc-add,
.wc-add:focus {
    opacity: 1;
}

.wc-day.weekend {
    background: #ececec;
}

.wc-day.leave {
    color: #fff;
    font-weight: 600;
    cursor: pointer;
}

.wc-day.leave.weekend {
    box-shadow: inset 0 0 0 999px rgba(0, 0, 0, 0.14);
}

.wc-day.half {
    color: #424242;
    font-weight: 700;
    cursor: pointer;
}

.wc-wfh-glyph {
    position: absolute;
    top: 1px;
    right: 3px;
    font-size: 11px;
    line-height: 1;
    color: rgba(255, 255, 255, 0.9);
    opacity: 0.9;
    z-index: 1;
    pointer-events: none;
}

.wc-day.half .wc-wfh-glyph {
    color: rgba(0, 0, 0, 0.55);
}

.wc-daynum {
    position: relative;
    z-index: 1;
}

.wc-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 16px;
    width: 100%;
    position: sticky;
    left: 0;
}

.wc-tip {
    position: fixed;
    z-index: 200;
    max-width: 260px;
    padding: 6px 10px;
    background: #23232e;
    color: #fff;
    font-size: 12px;
    line-height: 1.35;
    border-radius: 6px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
    pointer-events: none;
}

.wc-note-click {
    cursor: pointer;
}

.wc-note-marker {
    position: absolute;
    top: 0;
    right: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 9px 9px 0;
    border-color: transparent #e53935 transparent transparent;
    pointer-events: none;
}

.wc-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;

    .wc-legend-item {
        display: inline-flex;
        align-items: center;
        font-size: 0.75rem;
        color: #616161;

        i {
            width: 14px;
            height: 14px;
            border-radius: 4px;
            margin-right: 6px;
            display: inline-block;
        }

        i.wc-sw-pending {
            background: #9e9e9e;
            opacity: 0.55;
            box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.35);
        }

        i.wc-sw-half {
            background: linear-gradient(90deg, #9e9e9e 50%, #fff 50%);
            border: 1px solid #bdbdbd;
        }

        i.wc-sw-weekend {
            background: #ececec;
            border: 1px solid #d0d0d0;
        }

        i.wc-sw-note {
            position: relative;
            background: #fff;
            border: 1px solid #d0d0d0;
            overflow: hidden;

            &::after {
                content: '';
                position: absolute;
                top: 0;
                right: 0;
                border-style: solid;
                border-width: 0 7px 7px 0;
                border-color: transparent #e53935 transparent transparent;
            }
        }

        i.wc-legend-wfh {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            font-style: normal;
            line-height: 1;
            color: rgba(255, 255, 255, 0.9);
        }
    }
}

.wc-day.pending {
    opacity: 0.55;
    box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.35);
}

.wc-hcell.highlight {
    background: #fff8e1;
}

.wc-body-row.highlight-row .wc-left {
    background: #e3f2fd;
}
</style>
